import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Backdrop,
  Avatar,
  IconButton
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import HomeNavbar from '../components/home-nav-bar';
import ToastMessage from '../components/toast-message';
import SuccessDialog from '../components/success-dialog';
import uploadFileToCloud from '../utils/upload-file';
import { Delete, Edit, UploadFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function PeerReviewApplication() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [paper_types, setPaper_types] = useState({
    research_articles: false,
    review_articles: false,
    case_studies: false,
    others: false
  });
  const [otherPaper_types, setOtherPaper_types] = useState('');
  const [formDetails, setFormDetails] = useState({
    personalInfo: {
      name: '',
      title: '',
      affiliation: '',
      department: '',
      email: '',
      phone: '',
      profile_image: null,
    },
    expertise: '',
    experience: {
      cv: null,
      papers_reviewed_count: '',
      paper_types: [],
      otherPaper_types: ''
    },
    availability_and_commitment: ''
  });

  const [loading, setLoading] = useState(false);
  
  const [actionSuccessful, setActionSuccessful] = useState(false);

  const [status, setStatus] = useState({
      type: 0,
      message: ''
  });

  const clearToastMessge = () => {
      setStatus({
          type: 0,
          message: ''
      });
  };

  const steps = [
    'Personal Information',
    'Areas of Expertise',
    'Experience',
    'Availability & Commitment'
  ];

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      for (const key of Object.keys(formDetails.personalInfo)) {
        if (!formDetails.personalInfo[key]) {
          setStatus({
            type: 0,
            message: `${key} from section 1 is required`
          });
          return;
        }
      }

      if (!formDetails.expertise) {
        setStatus({
          type: 0,
          message: `areas of expertise is required`
        });
        return;
      }

      if (!formDetails.experience.cv) {
        setStatus({
          type: 0,
          message: `upload your cv`
        });
        return;
      }

      if (!formDetails.experience.papers_reviewed_count) {
        setStatus({
          type: 0,
          message: `number of papers reviewed is required`
        });
        return;
      }

      setLoading(true);

      const file = formDetails.personalInfo.profile_image;
      const email = formDetails.personalInfo.email.trim();

      const extension = file.name.split('.').pop(); 

      const fileName = `${email}.${extension}`;

      const path = `wajls/peer_reviewers/profile_images/${fileName}`;

      const downloadURL = await uploadFileToCloud(file, path, (progress) => {
        console.log(`Upload Progress: ${progress.toFixed(2)}%`);
      });

      const cvPath = `wajls/peer_reviewers/cvs/${formDetails.experience.cv.name}`;
      const cvDownloadUrl = await uploadFileToCloud(formDetails.experience.cv, cvPath, (progress) => {
        console.log(`Upload Progress: ${progress.toFixed(2)}%`);
      });


      const updatedFormDetails = {
        ...formDetails,
        personalInfo: {
          ...formDetails.personalInfo,
          profile_image: downloadURL
        },
        experience: {
          ...formDetails.experience,
          cv: cvDownloadUrl,
          paper_types: Object.keys(paper_types).filter(key => paper_types[key]),
          otherPaper_types: otherPaper_types
        }
      };
      setFormDetails(updatedFormDetails);

      try {
        const response = await axios.post('https://api.wajls.com/client/v1/join_peer_review.php', updatedFormDetails, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        setLoading(false);

        if (response.data.success) {
        setActionSuccessful(true);
        }
        else {
          setStatus({
            type: 0,
            message: response.data.message
          });
        }


      } catch (error) {
        setLoading(false);
        console.error();
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handlePersonalInfoChange("profile_image", file);
    }
  };


  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormDetails((prev) => ({
        ...formDetails,
        experience: {
          cv: file
        }
      }));
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setFormDetails({
      ...formDetails,
      personalInfo: {
        ...formDetails.personalInfo,
        [field]: value
      }
    });
  };

  const handleExpertiseChange = (value) => {
    setFormDetails({
      ...formDetails,
      expertise: value
    });
  };

  const handlePaperTypeChange = (event) => {
    setPaper_types({
      ...paper_types,
      [event.target.name]: event.target.checked
    });
  };

  const handleRadioChange = (value) => {
    setFormDetails({
      ...formDetails,
      availability_and_commitment: value
    });
  };

  const handlePapersReviewedChange = (value) => {
    setFormDetails({
      ...formDetails,
      experience: {
        ...formDetails.experience,
        papers_reviewed_count: value
      }
    });
  };

  const renderPersonalInfo = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            mb: 3,
            color: theme.palette.primary.main,
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            paddingBottom: 1,
            display: 'inline-block'
          }}
        >
          Personal Information
        </Typography>

        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar 
            src={
              formDetails.personalInfo.profile_image && URL.createObjectURL(formDetails.personalInfo.profile_image)
            } 
            sx={{ width: 80, height: 80, border: '1px solid #000000' }} />
            
          <IconButton title='upload profile image' color="primary" component="label">
              <Edit />
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </IconButton>
          <IconButton color="error" title='delete profile image' onClick={() => {
              handlePersonalInfoChange("profile_image", null);
          }}>
              <Delete />
          </IconButton>
        </Box>

        <TextField
          required
          fullWidth
          label="Full Name"
          variant="outlined"
          value={formDetails.personalInfo.name}
          onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
          sx={{ mb: 3, backgroundColor: '#fff' }}
        />
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Title/Position"
              variant="outlined"
              value={formDetails.personalInfo.title}
              onChange={(e) => handlePersonalInfoChange('title', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Affiliation/Organization"
              variant="outlined"
              value={formDetails.personalInfo.affiliation}
              onChange={(e) => handlePersonalInfoChange('affiliation', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
        </Grid>
        <TextField
          required
          fullWidth
          label="Department/Faculty"
          variant="outlined"
          value={formDetails.personalInfo.department}
          onChange={(e) => handlePersonalInfoChange('department', e.target.value)}
          sx={{ mt: 3, mb: 3, backgroundColor: '#fff' }}
        />
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              value={formDetails.personalInfo.email}
              onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Phone"
              variant="outlined"
              value={formDetails.personalInfo.phone}
              onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render the expertise section
  const renderExpertise = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            mb: 3,
            color: theme.palette.primary.main,
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            paddingBottom: 1,
            display: 'inline-block'
          }}
        >
          Areas of Expertise
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Please List your areas of expertise, separated by commas"
          multiline
          rows={5}
          value={formDetails.expertise}
          onChange={(e) => handleExpertiseChange(e.target.value)}
          sx={{ backgroundColor: '#fff' }}
        />
      </Box>
    );
  };

  // Render the experience section
  const renderExperience = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 3,
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            Experience in Peer-Review
          </Typography>

         

          <TextField
            required
            label="Number of papers reviewed in the last 3 years"
            type="number"
            variant="outlined"
            fullWidth
            value={formDetails.experience.papers_reviewed_count}
            onChange={(e) => handlePapersReviewedChange(e.target.value)}
            sx={{ mb: 3, backgroundColor: '#fff' }}
          />

           <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            sx={{ 
              textTransform: 'none', 
              justifyContent: 'flex-start', 
              backgroundColor: 'white',
              alignSelf: 'flex-start',
              mb: 2,
            }}
          >
            {formDetails.experience.cv ? formDetails.experience.cv.name : 'Upload CV (PDF or DOCX)'}
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              hidden
              onChange={handleCvUpload}
            />
          </Button>

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel
              component="legend"
              sx={{
                mb: 2,
                fontWeight: 500,
                color: theme.palette.text.primary
              }}
            >
              Types of papers reviewed:
            </FormLabel>
            <FormGroup>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paper_types.research_articles}
                        onChange={handlePaperTypeChange}
                        name="research_articles"
                        color="primary"
                      />
                    }
                    label="Research Articles"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paper_types.review_articles}
                        onChange={handlePaperTypeChange}
                        name="review_articles"
                        color="primary"
                      />
                    }
                    label="Review Articles"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paper_types.case_studies}
                        onChange={handlePaperTypeChange}
                        name="case_studies"
                        color="primary"
                      />
                    }
                    label="Case Studies"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paper_types.others}
                        onChange={handlePaperTypeChange}
                        name="others"
                        color="primary"
                      />
                    }
                    label="Others"
                  />
                </Grid>
              </Grid>
              {paper_types.others && (
                <TextField
                  label="Please specify other paper types"
                  variant="outlined"
                  fullWidth
                  value={otherPaper_types}
                  onChange={(e) => setOtherPaper_types(e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </FormGroup>
          </FormControl>
        </Box>

      </Box>
    );
  };

  // Render the availability section
  const renderAvailabilityAndCommitment = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 3,
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            Availability and Commitment
          </Typography>

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel
              component="legend"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: theme.palette.text.primary
              }}
            >
              Willing to review within tight deadlines (1–2 weeks)?
            </FormLabel>
            <RadioGroup
              row
              value={formDetails.availability_and_commitment}
              onChange={(e) => handleRadioChange(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ backgroundColor: "grey.100", minHeight: '100vh' }}>
      <HomeNavbar/>
       <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mb: 5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            Peer-Review Membership Application to West African Journal of Life Sciences
          </Typography>
        </Box>

        {/* Horizontal stepper for both mobile and desktop */}
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel sx={{ fontWeight: 'bold' }}></StepLabel>
              </Step>
            ))}
          </Stepper>

        </Box>

        {activeStep === 0 && renderPersonalInfo()}
        {activeStep === 1 && renderExpertise()}
        {activeStep === 2 && renderExperience()}
        {activeStep === 3 && renderAvailabilityAndCommitment()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            color="primary"
            endIcon={activeStep === steps.length - 1 ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {activeStep === steps.length - 1 ? 'Submit Application' : 'Continue'}
          </Button>
        </Box>
      </Box>

    </Container>
    
    <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>


    {status.message && <ToastMessage type={status.type} message={status.message} onClose={clearToastMessge}/>}

    <SuccessDialog open={actionSuccessful} article={false} email={formDetails.personalInfo.email} onClose={()=> {
      setActionSuccessful(false);
      navigate("/")
    }}/>
    </Box>
  );
}

export default PeerReviewApplication;